// src/components/ui/FundingAgentCard.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import clsx from 'clsx';
import { VisualElementV10 } from '@dopomogai/types';
import VisualElement from './VisualElement';
import { Button } from './Button';

export interface FundingAgentCardData {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  visual: VisualElementV10;
  creatorName: string;
  fundingGoalUSD: number;      // <-- CHANGED
  pledgedAmountUSD: number;    // <-- CHANGED
  supporterCount: number;
  communityAllocation: number;
}

interface FundingAgentCardProps {
  data: FundingAgentCardData;
  className?: string;
  onAction: (actionType: 'NAVIGATE_TO_AGENT_FUNDING_PAGE', payload: { slug: string }) => void;
  index: number;
}

export const FundingAgentCard: React.FC<FundingAgentCardProps> = ({
  data,
  className,
  onAction,
  index,
}) => {
  const { t } = useTranslation('co_ownership');

  const handleCardClick = () => {
    onAction('NAVIGATE_TO_AGENT_FUNDING_PAGE', { slug: data.slug });
  };
  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleCardClick();
  };

  const fundingProgress = data.fundingGoalUSD > 0
    ? Math.min(100, (data.pledgedAmountUSD / data.fundingGoalUSD) * 100)
    : 0;
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const formattedPledged = currencyFormatter.format(data.pledgedAmountUSD);
  const formattedGoal = currencyFormatter.format(data.fundingGoalUSD);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.1 }}
      className={clsx('relative group', className)}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}
      aria-label={`${t('showcase.ctaViewProject')}: ${data.name}`}
    >
      <div className="absolute inset-0 bg-black rounded-xl [.theme-liquid-glass_&]:hidden" />
      <div
        className={clsx(
          'relative bg-white rounded-xl border-2 border-black overflow-hidden flex flex-col h-full transition-transform duration-200 ease-out transform group-hover:-translate-x-1 group-hover:-translate-y-1 group-active:translate-x-0 group-active:translate-y-0 cursor-pointer',
          '[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
          '[.theme-liquid-glass_&]:rounded-3xl [.theme-liquid-glass_&]:backdrop-blur-xl',
          '[.theme-liquid-glass_&]:group-hover:translate-x-0 [.theme-liquid-glass_&]:group-hover:-translate-y-1'
        )}
      >
        <div className="relative overflow-hidden bg-slate-100 border-b-2 border-black aspect-[16/10]">
          <VisualElement visual={data.visual} />
        </div>
        <div className="flex flex-col flex-grow p-5">
          <div className="flex-grow">
            <h3 className="font-bold text-gray-900 text-lg leading-tight mb-1 [.theme-liquid-glass_&]:text-white">{data.name}</h3>
            <p className="text-slate-600 text-sm leading-relaxed mb-4 [.theme-liquid-glass_&]:text-white/70">{data.tagline}</p>
          </div>
          <div className="space-y-2 mb-4">
            <div className="w-full bg-slate-200 rounded-full h-2.5 border border-black/20 [.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:border-white/10">
              <div className="bg-emerald-500 h-full rounded-full [.theme-liquid-glass_&]:bg-violet-400/80" style={{ width: `${fundingProgress}%` }}></div>
            </div>
            <div className="flex justify-between items-center text-xs font-medium">
              <span className="text-emerald-600 [.theme-liquid-glass_&]:text-violet-300">{t('showcase.pledgedLabel')}: {formattedPledged}</span>
              <span className="text-slate-500 [.theme-liquid-glass_&]:text-white/60">{formattedGoal}</span>
            </div>
          </div>
          <div className="mt-auto pt-4 border-t border-slate-200 [.theme-liquid-glass_&]:border-white/10">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-3 [.theme-liquid-glass_&]:text-white/60">
              <span>
                {t('by', 'By')} <span className="font-semibold text-slate-700 [.theme-liquid-glass_&]:text-white/80">{data.creatorName}</span>
              </span>
            </div>
            <Button text={t('showcase.ctaViewProject')} variant="secondary" size="sm" fullWidth icon={<ArrowRight size={14} />} iconPosition="right" onClick={handleCtaClick} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};
